import { useNavigate } from 'react-router-dom';
import { Button, BookCover, Badge } from '@plush-book/ui';
import type { AnimalType, SceneType, CoverBackground } from '@plush-book/ui';
import { STORY_TEMPLATES } from '@plush-book/story-templates';
import { StepHeader } from '../components/StepHeader';
import { useGeneratorStore } from '../state/generatorStore';
import { api, ApiError } from '../api/client';

const ANIMAL_BY_STORY: Record<string, { animal: AnimalType; line1: string; line2: string }> = {
  sleep_star: { animal: 'bear', line1: 'Bruno &', line2: 'der Schlafstern' },
  little_big_adventure: { animal: 'rabbit', line1: 'Mimis großes', line2: 'Abenteuer' },
  birthday_surprise: { animal: 'dino', line1: 'Rexis große', line2: 'Überraschung' },
};

export function StorySelection() {
  const nav = useNavigate();
  const story = useGeneratorStore((s) => s.storyTemplateId);
  const setStory = useGeneratorStore((s) => s.setStory);
  const setBook = useGeneratorStore((s) => s.setBook);
  const bookId = useGeneratorStore((s) => s.bookId);

  async function next() {
    if (!story) return;
    if (!bookId) {
      try {
        const created = await api.createBook({ storyTemplateId: story });
        setBook(created.id, created.publicId);
      } catch (err) {
        if (err instanceof ApiError) {
          console.warn('createBook failed (continuing without API):', err.status);
        } else {
          console.warn(err);
        }
      }
    } else {
      api.updateBook(bookId, { storyTemplateId: story }).catch(() => {});
    }
    nav('/plush');
  }

  return (
    <div className="shell">
      <StepHeader active={0} />
      <div className="scroll" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <h2
          style={{
            fontFamily: 'var(--serif)',
            fontSize: 28,
            fontWeight: 500,
            margin: 0,
            letterSpacing: -0.3,
          }}
        >
          Wähle eine Geschichte
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 6, marginBottom: 22 }}>
          Welcher Moment passt zu eurem Kuscheltier?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {STORY_TEMPLATES.map((s) => {
            const sel = story === s.id;
            const meta = ANIMAL_BY_STORY[s.id]!;
            return (
              <div
                key={s.id}
                onClick={() => setStory(s.id)}
                style={{
                  background: 'var(--bg-card)',
                  border: sel ? '2px solid var(--terracotta)' : '1px solid var(--line)',
                  borderRadius: 18,
                  padding: 14,
                  display: 'flex',
                  gap: 14,
                  position: 'relative',
                  cursor: 'pointer',
                  boxShadow: sel ? '0 6px 16px -8px rgba(184,106,76,0.4)' : 'none',
                }}
              >
                <div style={{ width: 80, flexShrink: 0 }}>
                  <BookCover
                    background={s.cover.background as CoverBackground}
                    scene={s.cover.scene as SceneType}
                    animal={meta.animal}
                    titleLine1={meta.line1}
                    titleLine2={meta.line2}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Badge tone="sand" style={{ marginBottom: 6 }}>
                    {s.badge}
                  </Badge>
                  <div
                    style={{
                      fontFamily: 'var(--serif)',
                      fontSize: 18,
                      fontWeight: 500,
                      lineHeight: 1.15,
                      marginBottom: 4,
                    }}
                  >
                    {s.title}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
                    {s.shortDescription}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--ink-mute)',
                      marginTop: 6,
                      fontFamily: 'var(--mono)',
                      letterSpacing: 0.5,
                    }}
                  >
                    {s.ageRange.toUpperCase()} · {s.useCase.toUpperCase()}
                  </div>
                </div>
                {sel && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: 'var(--terracotta)',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 11,
                    }}
                  >
                    ✓
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="sticky-cta" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <Button variant="dark" size="md" fullWidth disabled={!story} onClick={next}>
          Diese Geschichte auswählen →
        </Button>
      </div>
    </div>
  );
}
